import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { contributionService } from '../../services/contributionService';
import { formatCurrency } from '../../utils/helpers';

const CYCLES = [['weekly', 'Weekly'], ['biweekly', 'Biweekly'], ['monthly', 'Monthly']];

function StepIndicator({ step }) {
  const steps = ['Goal & Amount', 'Cycle', 'Review'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '1.75rem', maxWidth: 640 }}>
      {steps.map((label, idx) => {
        const num = idx + 1;
        const active = step === num;
        const done = step > num;
        return (
          <div key={num} style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: done ? '#00C878' : active ? '#1596E0' : 'rgba(255,255,255,0.08)',
                border: `2px solid ${done ? '#00C878' : active ? '#1596E0' : 'rgba(255,255,255,0.15)'}`,
                fontSize: '0.8rem', fontWeight: 700,
                color: (done || active) ? '#fff' : 'var(--text-3)',
                transition: 'all 0.2s',
              }}>
                {done ? '✓' : num}
              </div>
              <span style={{ fontSize: '0.7rem', color: active ? 'var(--text-1)' : 'var(--text-3)', fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
                {label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: done ? '#00C878' : 'rgba(255,255,255,0.1)', margin: '0 4px', marginBottom: 20, transition: 'background 0.3s' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function NewContributionPlan() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [form, setForm] = useState({
    goal_name: '', goal_amount: '', contribution_amount: '', cycle: 'monthly', duration_cycles: '',
  });
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const fetchPreview = useCallback(async (f) => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await contributionService.previewSchedule({
        goal_amount: f.goal_amount, contribution_amount: f.contribution_amount,
        cycle: f.cycle, duration_cycles: f.duration_cycles,
      });
      setPreview(res.data.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Could not compute preview.');
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const goToStep2 = () => {
    if (!form.goal_name || !form.goal_amount || !form.contribution_amount) return;
    setStep(2);
  };

  const goToStep3 = () => {
    if (!form.duration_cycles) return;
    fetchPreview(form);
    setStep(3);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await contributionService.createPlan(form);
      navigate(`/contributions/${res.data.data.id}`, { state: { success: 'Savings plan created!' } });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create plan.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h1>New Savings Plan</h1>
        <p>Set a goal and start contributing toward it</p>
      </div>

      <StepIndicator step={step} />

      {step === 1 && (
        <div className="card">
          <div className="form-group">
            <label className="form-label">Goal Name</label>
            <input className="form-input" name="goal_name" value={form.goal_name} onChange={handleChange} placeholder="e.g. New Laptop" />
          </div>
          <div className="form-group">
            <label className="form-label">Goal Amount (₦)</label>
            <input className="form-input" type="number" name="goal_amount" value={form.goal_amount} onChange={handleChange} placeholder="e.g. 120000" />
          </div>
          <div className="form-group">
            <label className="form-label">Contribution Amount Per Cycle (₦)</label>
            <input className="form-input" type="number" name="contribution_amount" value={form.contribution_amount} onChange={handleChange} placeholder="e.g. 10000" />
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={goToStep2}>Continue →</button>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <div className="form-group">
            <label className="form-label">Contribution Cycle</label>
            <select className="form-input" name="cycle" value={form.cycle} onChange={handleChange}>
              {CYCLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Number of Contributions</label>
            <input className="form-input" type="number" name="duration_cycles" value={form.duration_cycles} onChange={handleChange} placeholder="e.g. 12" />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" style={{ flex: 2 }} onClick={goToStep3}>Preview →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          {previewLoading ? (
            <p style={{ color: 'var(--text-2)' }}>Calculating schedule…</p>
          ) : previewError ? (
            <div className="alert alert-error"><span>⚠</span><span>{previewError}</span></div>
          ) : preview && (
            <>
              {!preview.meets_goal && (
                <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                  <span>⚠</span><span>Your planned contributions total {formatCurrency(preview.total_planned_contributions)}, which is less than your goal of {formatCurrency(preview.goal_amount)}. Consider increasing the amount or duration.</span>
                </div>
              )}
              <div className="grid-2" style={{ marginBottom: '1.25rem' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase' }}>Goal</div>
                  <div style={{ fontWeight: 700 }}>{formatCurrency(preview.goal_amount)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase' }}>Total Planned</div>
                  <div style={{ fontWeight: 700, color: '#00d9a5' }}>{formatCurrency(preview.total_planned_contributions)}</div>
                </div>
              </div>
              <div className="table-wrapper" style={{ maxHeight: 260, overflowY: 'auto', marginBottom: '1.25rem' }}>
                <table>
                  <thead><tr><th>#</th><th>Due Date</th><th>Amount</th></tr></thead>
                  <tbody>
                    {preview.schedule.map(row => (
                      <tr key={row.installment}>
                        <td style={{ textAlign: 'center' }}>{row.installment}</td>
                        <td>{row.due_date}</td>
                        <td>{formatCurrency(row.amount_due)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span><span>{error}</span></div>}
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setStep(2)}>← Back</button>
                <button className="btn btn-primary" style={{ flex: 2 }} disabled={loading} onClick={handleSubmit}>
                  {loading ? 'Creating…' : 'Create Plan'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
